import React, { useEffect, useState } from "react";
import {
  DeleteOutlined,
  FormOutlined,
  LeftOutlined,
  RightOutlined,
} from "@ant-design/icons";
import { Link } from "react-router-dom";
import axios from "axios";
import { Modal, Button } from "react-bootstrap";

const Products = ({
  searchKeyword = "",
  sortBy = "",
  sortOrder = "asc",
  currentPage = 1,
  onPageChange,
}) => {
  const [products, setProducts] = useState([]);
  const [totalProducts, setTotalProducts] = useState(0);
  const [deleteId, setDeleteId] = useState("");
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const pageSize = 10;

  // Fetch products với API mới
  useEffect(() => {
    fetchProducts();
  }, [searchKeyword, sortBy, sortOrder, currentPage]);

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: currentPage,
        pageSize: pageSize.toString(),
      });

      if (searchKeyword.trim()) {
        params.set("search", searchKeyword.trim());
      }
      if (sortBy) {
        params.set("sortBy", sortBy);
        params.set("sortOrder", sortOrder);
      }

      const response = await axios.get(
        `${
          process.env.REACT_APP_API_URL
        }/api/products/getall?${params.toString()}`
      );

      setProducts(response.data.products || []);
      setTotalProducts(response.data.totalProducts || 0);
    } catch (error) {
      console.error("Error fetching products:", error);
      setProducts([]);
      setTotalProducts(0);
    } finally {
      setLoading(false);
    }
  };

  const handleClickDelete = (id) => {
    setDeleteId(id);
    setShow(true);
  };

  const handleClose = () => {
    setShow(false);
  };

  const handleDeleteItem = () => {
    const token = localStorage.getItem("accessToken");
    const config = {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    };

    axios
      .delete(
        `${process.env.REACT_APP_API_URL}/api/products/delete/${deleteId}`,
        config
      )
      .then(() => {
        setShow(false);
        fetchProducts(); // Refresh data thay vì reload page
      })
      .catch((error) => {
        console.log(error);
        setShow(false);
      });
  };

  const handlePageChange = (newPage) => {
    if (onPageChange) {
      onPageChange(newPage);
    }
  };

  // Pagination logic
  const totalPages = Math.ceil(totalProducts / pageSize);
  const pagesToShow = 5;

  let startPage = Math.max(
    1,
    parseInt(currentPage) - Math.floor(pagesToShow / 2)
  );
  let endPage = startPage + pagesToShow - 1;

  if (endPage > totalPages) {
    endPage = totalPages;
    startPage = Math.max(1, endPage - pagesToShow + 1);
  }

  let pages = [];
  if (totalPages > 0) {
    pages = Array.from(
      { length: endPage - startPage + 1 },
      (_, i) => startPage + i
    );
  }

  if (loading) {
    return (
      <div className="p-8 text-center">
        <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
        <p className="mt-4 text-gray-600 text-lg">Đang tải dữ liệu...</p>
        <p className="mt-2 text-gray-500 text-sm">
          Vui lòng chờ trong giây lát
        </p>
      </div>
    );
  }

  return (
    <div className="h-full max-h-[calc(100vh-200px)] overflow-y-auto">
      <div className="p-6">
        {/* Delete Confirmation Modal */}
        <Modal show={show} onHide={handleClose}>
          <Modal.Header closeButton>
            <Modal.Title>Xóa sản phẩm này?</Modal.Title>
          </Modal.Header>
          <Modal.Body>Bạn có chắc muốn xóa sản phẩm này không?</Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleDeleteItem}>
              Đồng ý
            </Button>
            <Button variant="primary" onClick={handleClose}>
              Hủy
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Results Summary */}
        {totalProducts > 0 && (
          <div className="mb-4 text-sm text-gray-600">
            Hiển thị {(parseInt(currentPage) - 1) * pageSize + 1} -{" "}
            {Math.min(parseInt(currentPage) * pageSize, totalProducts)} trong
            tổng số {totalProducts} sản phẩm
            {searchKeyword && (
              <span className="ml-2 text-blue-600">
                (tìm kiếm: "{searchKeyword}")
              </span>
            )}
          </div>
        )}

        {/* Products Table */}
        {products.length > 0 ? (
          <div className="overflow-x-auto max-h-[500px]  overflow-y-auto">
            <table className="min-w-full bg-white border border-gray-200 rounded-lg">
              <thead className="bg-gray-50 sticky top-0 z-10">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-16">
                    STT
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    SKU
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-20">
                    Hình
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Tên sản phẩm
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Giá bán
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Số lượng
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Loại SP
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Trạng thái
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-28">
                    Hành động
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {products.map((productData, index) => {
                  const product = productData.product;
                  let imageUrl = product.urlImage;

                  // Clean up image URL
                  const duplicateURL = `${process.env.REACT_APP_API_URL}/${process.env.REACT_APP_API_URL}/`;
                  if (imageUrl.startsWith(duplicateURL)) {
                    imageUrl = imageUrl.replace(duplicateURL, "");
                  }

                  return (
                    <tr key={product._id} className="hover:bg-gray-50">
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                        {(parseInt(currentPage) - 1) * pageSize + index + 1}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {product.sku}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <img
                          src={
                            imageUrl?.startsWith("https")
                              ? imageUrl
                              : `${process.env.REACT_APP_API_URL}/${imageUrl}`
                          }
                          alt="Product"
                          loading="lazy"
                          className="h-12 w-12 object-cover rounded-lg border border-gray-200 transition-opacity duration-200 hover:opacity-80"
                          onError={(e) => {
                            // Tránh infinite loop bằng cách kiểm tra src hiện tại
                            if (!e.target.src.includes("data:image")) {
                              e.target.src =
                                "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDgiIGhlaWdodD0iNDgiIHZpZXdCb3g9IjAgMCA0OCA0OCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3Qgd2lkdGg9IjQ4IiBoZWlnaHQ9IjQ4IiBmaWxsPSIjRjNGNEY2Ii8+CjxwYXRoIGQ9Ik0yNCAzNkMzMC42Mjc0IDM2IDM2IDMwLjYyNzQgMzYgMjRDMzYgMTcuMzcyNiAzMC42Mjc0IDEyIDI0IDEyQzE3LjM3MjYgMTIgMTIgMTcuMzcyNiAxMiAyNEMxMiAzMC42Mjc0IDE3LjM3MjYgMzYgMjQgMzZaIiBzdHJva2U9IiM5Q0EzQUYiIHN0cm9rZS13aWR0aD0iMiIgc3Ryb2tlLWxpbmVjYXA9InJvdW5kIiBzdHJva2UtbGluZWpvaW49InJvdW5kIi8+CjxwYXRoIGQ9Ik0xNiAzMkwyNCAyNEwzMiAzMiIgc3Ryb2tlPSIjOUNBM0FGIiBzdHJva2Utd2lkdGg9IjIiIHN0cm9rZS1saW5lY2FwPSJyb3VuZCIgc3Ryb2tlLWxpbmVqb2luPSJyb3VuZCIvPgo8L3N2Zz4K";
                            }
                          }}
                        />
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-900 max-w-xs">
                        <div className="truncate" title={product.productName}>
                          {product.productName}
                        </div>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                        {product.sellingPrice.toLocaleString("vi-VN", {
                          style: "currency",
                          currency: "VND",
                        })}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                        <span
                          className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            product.quantity > 10
                              ? "bg-green-100 text-green-800"
                              : product.quantity > 0
                              ? "bg-yellow-100 text-yellow-800"
                              : "bg-red-100 text-red-800"
                          }`}
                        >
                          {product.quantity}
                        </span>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                        {productData.category?.categoryName ||
                          product.categoryId}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <span
                          className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            product.status
                              ? "bg-green-100 text-green-800"
                              : "bg-red-100 text-red-800"
                          }`}
                        >
                          {product.status ? "Active" : "Inactive"}
                        </span>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex space-x-3">
                          <Link
                            to={`/admin/products/edit/${product.sku}`}
                            className="text-blue-600 hover:text-blue-900 transition-colors"
                            title="Chỉnh sửa"
                          >
                            <FormOutlined className="text-lg" />
                          </Link>
                          <button
                            onClick={() => handleClickDelete(product.sku)}
                            className="text-red-600 hover:text-red-900 transition-colors"
                            title="Xóa"
                          >
                            <DeleteOutlined className="text-lg" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-12">
            <div className="text-gray-400 text-6xl mb-4">📦</div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {searchKeyword
                ? "Không tìm thấy sản phẩm"
                : "Không có sản phẩm nào"}
            </h3>
            <p className="text-gray-500">
              {searchKeyword
                ? `Không có sản phẩm nào phù hợp với từ khóa "${searchKeyword}"`
                : "Hãy thêm sản phẩm đầu tiên của bạn"}
            </p>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between mt-6 px-4 py-3 bg-white border border-gray-200 rounded-lg">
            <div className="text-sm text-gray-700">
              Trang {currentPage} / {totalPages}
            </div>

            <div className="flex items-center space-x-2">
              {/* Previous Button */}
              {parseInt(currentPage) > 1 && (
                <button
                  onClick={() => handlePageChange(parseInt(currentPage) - 1)}
                  className="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 hover:text-gray-700 transition-colors"
                >
                  <LeftOutlined className="mr-1" />
                  Trước
                </button>
              )}

              {/* Page Numbers */}
              <div className="flex space-x-1">
                {startPage > 1 && (
                  <>
                    <button
                      onClick={() => handlePageChange(1)}
                      className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 hover:text-gray-700 transition-colors"
                    >
                      1
                    </button>
                    {startPage > 2 && (
                      <span className="px-3 py-2 text-sm text-gray-500">
                        ...
                      </span>
                    )}
                  </>
                )}

                {pages.map((page) => (
                  <button
                    key={page}
                    onClick={() => handlePageChange(page)}
                    className={`px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                      parseInt(currentPage) === page
                        ? "text-white bg-blue-600 border border-blue-600"
                        : "text-gray-500 bg-white border border-gray-300 hover:bg-gray-50 hover:text-gray-700"
                    }`}
                  >
                    {page}
                  </button>
                ))}

                {endPage < totalPages && (
                  <>
                    {endPage < totalPages - 1 && (
                      <span className="px-3 py-2 text-sm text-gray-500">
                        ...
                      </span>
                    )}
                    <button
                      onClick={() => handlePageChange(totalPages)}
                      className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 hover:text-gray-700 transition-colors"
                    >
                      {totalPages}
                    </button>
                  </>
                )}
              </div>

              {/* Next Button */}
              {parseInt(currentPage) < totalPages && (
                <button
                  onClick={() => handlePageChange(parseInt(currentPage) + 1)}
                  className="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 hover:text-gray-700 transition-colors"
                >
                  Sau
                  <RightOutlined className="ml-1" />
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Products;
